import React, { FC, useContext, useEffect, useMemo } from 'react';

import { GuestRouter, GuestRoutes } from './routers/GuestRouter';
import { Route, Switch, useHistory } from 'react-router-dom';
import { AuthenticatedRouter } from './routers/AuthenticatedRouter';
import { AuthContext } from './contexts/AuthContext';
import { RedirectToAuth } from './routers/RedirectToAuth';
import { authenticatedRoot, AuthenticatedRoutes } from './routers/AuthenticatedRoutes';
import { EOrganizationSelectionStatus, OrganizationContext } from './contexts/OrganizationContext';
import { is2FaTokenData, parseAccessToken } from './helpers/parseAccessToken';
import { NoOrganizationSelectedRouter } from './routers/NoOrganizationSelectedRouter';
import { CurrentUserContextProvider } from './contexts/CurrentUserContextProvider';
import { OrganizationContextProvider } from './contexts/OrganizationContextProvider';
import { PermissionContextProvider } from './contexts/PermissionContextProvider';
import { LazyUsersContextProvider } from './contexts/LazyUsersContextProvider';
import { LazyOrganizationContextProvider } from './contexts/LazyOrganizationContextProvider';
import { UserDownloadsFetcher } from './fetchers/UserDownloadsFetcher';

const OrgSelectionSentinel = () => {
  const { identity } = useContext(AuthContext);
  const { selectionStatus } = useContext(OrganizationContext);

  const isMfaRequired = useMemo(() => {
    if (identity) {
      const parsed = parseAccessToken(identity.tokenData.access_token);
      return is2FaTokenData(parsed.data);
    }
    return false;
  }, [identity?.tokenData?.access_token]);

  return (
    <>
      {identity && !isMfaRequired ? (
        selectionStatus === EOrganizationSelectionStatus.NotSelected ? (
          <NoOrganizationSelectedRouter />
        ) : (
          <UserDownloadsFetcher.WMF>
            <AuthenticatedRouter />
          </UserDownloadsFetcher.WMF>
        )
      ) : null}
      {!identity ? (
        <Switch>
          <RedirectToAuth from={authenticatedRoot} />
        </Switch>
      ) : null}
    </>
  );
};

const legacyRedirects: Record<string, string> = {
  '/customer/user/support': AuthenticatedRoutes.Support(),
  '/customer/user/signin': GuestRoutes.SignIn(),
  '/customer/user/licenses': AuthenticatedRoutes.Licenses(),
  '/customer/user/all-licenses': AuthenticatedRoutes.AllLicenses(),
};

const App: FC = () => {
  const history = useHistory();

  useEffect(() => {
    const search = new URLSearchParams(history.location.search);
    const redirect = search.get('redirect');
    let { pathname } = history.location;

    if (pathname in legacyRedirects) {
      pathname = legacyRedirects[pathname];
    } else if (pathname.startsWith('/customer/') || pathname.startsWith('/admin/')) {
      pathname = '/';
    }
    if (redirect && redirect in legacyRedirects) {
      search.set('redirect', legacyRedirects[redirect]);
    }
    const searchStr = search.toString() ? `?${search.toString()}` : '';
    history.replace(`${pathname}${searchStr}`);
  }, []);

  return (
    <>
      <Route path={authenticatedRoot}>
        <CurrentUserContextProvider>
          <OrganizationContextProvider>
            <PermissionContextProvider>
              <LazyUsersContextProvider>
                <LazyOrganizationContextProvider>
                  <OrgSelectionSentinel />
                </LazyOrganizationContextProvider>
              </LazyUsersContextProvider>
            </PermissionContextProvider>
          </OrganizationContextProvider>
        </CurrentUserContextProvider>
      </Route>

      <GuestRouter />
    </>
  );
};
export default App;
